import { useMemo } from "react";
import { useGetSubscriptionPlansQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { GetSubscriptionPlansResponse } from "@/redux/apis/subscription/types";

interface PlanLimits {
  maxVehicles: number;
  maxDrivers: number;
}

const UNLIMITED = 100000;

const FALLBACK_LIMITS: Record<string, PlanLimits> = {
  solo: { maxVehicles: 1, maxDrivers: 1 },
  fleet: { maxVehicles: 10, maxDrivers: 20 },
  enterprise: { maxVehicles: UNLIMITED, maxDrivers: UNLIMITED },
};

/**
 * Extract plan limits from the subscription plans API response.
 * Looks for features with slugs "max-driver-count" and "max-vehicle-count"
 * and reads the defaultValue for each plan.
 */
function extractPlanLimits(data: GetSubscriptionPlansResponse): Record<string, PlanLimits> {
  const limits: Record<string, PlanLimits> = {};

  const allFeatures = data.featureGroups.flatMap((group) => group.features);
  const driverFeature = allFeatures.find((f) => f.slug === "max-driver-count");
  const vehicleFeature = allFeatures.find((f) => f.slug === "max-vehicle-count");

  for (const sub of data.subscriptions) {
    const slug = sub.slug.toLowerCase();
    const driverSub = driverFeature?.subscriptions.find((s) => s.subscriptionId === sub.subscriptionId);
    const vehicleSub = vehicleFeature?.subscriptions.find((s) => s.subscriptionId === sub.subscriptionId);

    const driverValue = driverSub?.defaultValue;
    const vehicleValue = vehicleSub?.defaultValue;

    limits[slug] = {
      maxDrivers: driverValue && driverValue.toLowerCase() !== "unlimited" ? Number(driverValue) : UNLIMITED,
      maxVehicles: vehicleValue && vehicleValue.toLowerCase() !== "unlimited" ? Number(vehicleValue) : UNLIMITED,
    };
  }

  return limits;
}

/**
 * Hook to get subscription plan limits from the API.
 * Returns a getPlanLimits function and a shouldShowLimitIndicator function
 * that use real API data instead of hardcoded values.
 */
export const useSubscriptionPlanLimits = () => {
  const { data } = useGetSubscriptionPlansQuery();

  const planLimitsMap = useMemo(() => {
    if (!data) return FALLBACK_LIMITS;
    return extractPlanLimits(data);
  }, [data]);

  const getPlanLimits = (planSlug: string): PlanLimits => {
    const slug = planSlug.toLowerCase();
    return planLimitsMap[slug] ?? FALLBACK_LIMITS.solo;
  };

  const shouldShowLimitIndicator = (planSlug: string): boolean => {
    const limits = getPlanLimits(planSlug);
    return limits.maxVehicles < UNLIMITED || limits.maxDrivers < UNLIMITED;
  };

  return { getPlanLimits, shouldShowLimitIndicator };
};
